import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { AlbumsIcon, PlusIcon } from '../components/Icons';
import { BlockingProgress } from '../components/BlockingProgress';
import { api, thumbUrl, type Album } from '../lib/api';
import { readCachedAlbums, writeCachedAlbumCover, writeCachedAlbumList } from '../lib/albumCache';

/** Albums are just folders in the shared Nextcloud directory. */
export function AlbumsPage() {
  const { user } = useAuth();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [covers, setCovers] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [newName, setNewName] = useState('');
  const [nextcloudUrl, setNextcloudUrl] = useState<string | null>(null);
  const albumNameRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.albums();
      setAlbums(result.albums);
      setError(null);
      if (user) void writeCachedAlbumList(user.id, result.albums);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load albums.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let current = true;
    void (async () => {
      const cached = user ? await readCachedAlbums(user.id) : null;
      if (!current) return;
      if (cached) {
        setAlbums(cached.albums);
        setCovers(cached.covers);
        setLoading(false);
      }
      if (current) await load();
    })();
    return () => {
      current = false;
    };
  }, [load, user]);

  const resolveCover = useCallback((album: Album, fileId: string | null) => {
    setCovers((current) => ({ ...current, [album.path]: fileId }));
    if (user) void writeCachedAlbumCover(user.id, album, fileId);
  }, [user]);

  useEffect(() => {
    void api.config().then((config) => setNextcloudUrl(config.nextcloudUrl)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (creating) albumNameRef.current?.focus();
  }, [creating]);

  const createAlbum = async () => {
    const name = newName.trim();
    if (!name || creatingAlbum) return;

    setCreatingAlbum(true);
    setError(null);
    try {
      await api.createAlbum(name);
      setNewName('');
      setCreating(false);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create the album.');
    } finally {
      setCreatingAlbum(false);
    }
  };

  return (
    <>
      <header className="app-header">
        <h1>Albums</h1>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setCreating((value) => !value)}
          disabled={creatingAlbum}
          aria-expanded={creating}
          aria-controls="new-album-form"
        >
          <PlusIcon className="" />
          New album
        </button>
      </header>

      <div className="page" aria-busy={creatingAlbum}>
        {creating && (
          <div className="card" id="new-album-form">
            <h2>New album</h2>
            <div className="composer" style={{ paddingTop: 4 }}>
              <textarea
                ref={albumNameRef}
                rows={1}
                value={newName}
                placeholder="Album name"
                aria-label="Album name"
                maxLength={200}
                disabled={creatingAlbum}
                onChange={(event) => setNewName(event.target.value)}
                onKeyDown={(event) => {
                  if (creatingAlbum) return;
                  if (event.key === 'Escape') {
                    setCreating(false);
                    setNewName('');
                    return;
                  }
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void createAlbum();
                  }
                }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void createAlbum()}
                disabled={!newName.trim() || creatingAlbum}
                aria-busy={creatingAlbum}
              >
                {creatingAlbum ? 'Creating album…' : 'Create'}
              </button>
            </div>
          </div>
        )}

        {creatingAlbum && (
          <BlockingProgress
            title="Creating your album…"
            detail="Making room for these memories. This may take a moment."
          />
        )}

        {error && (
          <div className="banner error">
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="center-fill">
            <div className="spinner" role="status" aria-label="Loading albums" />
          </div>
        ) : albums.length === 0 ? (
          <div className="empty-state">
            <AlbumsIcon />
            <h2>No albums yet</h2>
            <p>Create one for a trip or occasion, or keep browsing the photos your family shared.</p>
            <div className="albums-empty-actions">
              <Link className="btn btn-primary" to="/">Browse family photos</Link>
              {nextcloudUrl && (
                <a
                  className="text-button"
                  href={new URL('/index.php/apps/memories/', nextcloudUrl).toString()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Search in Memories
                </a>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 14,
            }}
          >
            {albums.map((album) => (
              <Link key={album.name} to={`/album/${encodeURIComponent(album.name)}`}>
                <div
                  style={{
                    aspectRatio: '1',
                    borderRadius: 'var(--radius)',
                    overflow: 'hidden',
                    background: 'var(--bg-raised)',
                    border: '1px solid var(--border)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <LazyAlbumCover album={album} cover={covers[album.path]} onResolved={resolveCover} />
                </div>
                <div
                  style={{
                    marginTop: 7,
                    fontSize: '0.9rem',
                    fontWeight: 570,
                    color: 'var(--text)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {album.name}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function LazyAlbumCover({ album, cover, onResolved }: {
  album: Album;
  cover: string | null | undefined;
  onResolved: (album: Album, fileId: string | null) => void;
}) {
  const target = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cover !== undefined) return;
    const element = target.current;
    if (!element) return;
    let current = true;
    const loadCover = () => {
      void api.albumPhotos(album.path).then((result) => {
        if (current) onResolved(album, result.photos[0]?.fileId ?? null);
      }).catch(() => undefined);
    };
    if (typeof IntersectionObserver === 'undefined') {
      loadCover();
      return () => { current = false; };
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      loadCover();
    }, { rootMargin: '240px 0px' });
    observer.observe(element);
    return () => {
      current = false;
      observer.disconnect();
    };
  }, [album, cover, onResolved]);

  return <div ref={target} className="album-cover-content">
    {cover ? <img src={thumbUrl(cover, 400)} alt="" loading="lazy" /> : <AlbumsIcon className="" />}
  </div>;
}
